import { useState, useEffect, FormEvent } from 'react';
import { supabase } from '@/lib/supabaseClient';
import ImageUploader from '@/components/base/ImageUploader';

interface Category {
  id: string;
  name_de: string;
  name_en: string;
}

interface Product {
  id?: string;
  category_id: string;
  name_de: string;
  name_en: string;
  description_de: string;
  description_en: string;
  price: number;
  image_url: string;
  active: boolean;
  popular: boolean;
  badge_de: string;
  badge_en: string;
  sort_order: number;
}

interface ProductFormModalProps {
  product: Product | null;
  categories: Category[];
  onClose: () => void;
  onSaved: () => void;
}

const emptyProduct = (categories: Category[]): Product => ({
  category_id: categories[0]?.id ?? '',
  name_de: '',
  name_en: '',
  description_de: '',
  description_en: '',
  price: 0,
  image_url: '',
  active: true,
  popular: false,
  badge_de: '',
  badge_en: '',
  sort_order: 0,
});

const ProductFormModal = ({ product, categories, onClose, onSaved }: ProductFormModalProps) => {
  const isEdit = !!product?.id;
  const [form, setForm] = useState<Product>(product ?? emptyProduct(categories));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(product ?? emptyProduct(categories));
    setError(null);
  }, [product, categories]);

  const set = (field: keyof Product, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.name_de.trim()) { setError('Ürün adı (Almanca) zorunludur.'); return; }
    if (!form.category_id) { setError('Kategori seçiniz.'); return; }
    if (form.price <= 0) { setError('Fiyat 0\'dan büyük olmalıdır.'); return; }

    setSaving(true);
    setError(null);

    const payload = {
      category_id: form.category_id,
      name_de: form.name_de.trim(),
      name_en: form.name_en.trim() || form.name_de.trim(),
      description_de: form.description_de.trim(),
      description_en: form.description_en.trim() || form.description_de.trim(),
      price: Number(form.price),
      image_url: form.image_url.trim(),
      active: form.active,
      popular: form.popular,
      badge_de: form.badge_de.trim() || null,
      badge_en: form.badge_en.trim() || null,
      sort_order: Number(form.sort_order),
    };

    let err;
    if (isEdit) {
      ({ error: err } = await supabase.from('products').update(payload).eq('id', product!.id!));
    } else {
      ({ error: err } = await supabase.from('products').insert(payload));
    }

    setSaving(false);
    if (err) { setError('Kayıt sırasında hata: ' + err.message); return; }
    onSaved();
  };

  const inputCls = 'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 placeholder-gray-400 outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-50 transition-all';
  const labelCls = 'block text-xs font-semibold text-gray-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h2 className="text-base font-bold text-gray-900">
            {isEdit ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
          </h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">
          {/* Category */}
          <div>
            <label className={labelCls}>Kategori *</label>
            <select
              value={form.category_id}
              onChange={(e) => set('category_id', e.target.value)}
              className={inputCls}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name_de}</option>
              ))}
            </select>
          </div>

          {/* Names */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Ürün Adı (Almanca) *</label>
              <input
                type="text"
                value={form.name_de}
                onChange={(e) => set('name_de', e.target.value)}
                placeholder="z.B. Döner im Fladenbrot"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Ürün Adı (İngilizce)</label>
              <input
                type="text"
                value={form.name_en}
                onChange={(e) => set('name_en', e.target.value)}
                placeholder="e.g. Doner in Flatbread"
                className={inputCls}
              />
            </div>
          </div>

          {/* Descriptions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Açıklama (Almanca)</label>
              <textarea
                value={form.description_de}
                onChange={(e) => set('description_de', e.target.value)}
                placeholder="Ürün açıklaması..."
                rows={3}
                maxLength={500}
                className={`${inputCls} resize-none`}
              />
            </div>
            <div>
              <label className={labelCls}>Açıklama (İngilizce)</label>
              <textarea
                value={form.description_en}
                onChange={(e) => set('description_en', e.target.value)}
                placeholder="Product description..."
                rows={3}
                maxLength={500}
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>

          {/* Price & Sort */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Fiyat (CHF) *</label>
              <input
                type="number"
                step="0.50"
                min="0"
                value={form.price}
                onChange={(e) => set('price', e.target.value)}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Sıralama</label>
              <input
                type="number"
                min="0"
                value={form.sort_order}
                onChange={(e) => set('sort_order', e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          {/* Image Upload */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-100 rounded-xl">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                <i className="ri-image-line text-amber-500 text-sm"></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-amber-700">Önerilen boyut: <span className="font-bold">600 × 400 px</span> (3:2 yatay oran)</p>
                <p className="text-xs text-amber-600 mt-0.5">Menüde kare (80×80px) ve detay panelinde büyük gösterilir. JPG/PNG, max 5MB.</p>
              </div>
            </div>
            <ImageUploader
              value={form.image_url}
              onChange={(url) => set('image_url', url)}
              label="Ürün Görseli (600 × 400 px önerilir)"
              placeholder="https://... veya fotoğraf yükle"
              folder="products"
            />
          </div>

          {/* Badges */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Rozet (Almanca)</label>
              <input
                type="text"
                value={form.badge_de}
                onChange={(e) => set('badge_de', e.target.value)}
                placeholder="z.B. Beliebt, Neu"
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Rozet (İngilizce)</label>
              <input
                type="text"
                value={form.badge_en}
                onChange={(e) => set('badge_en', e.target.value)}
                placeholder="e.g. Popular, New"
                className={inputCls}
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <div
                onClick={() => set('active', !form.active)}
                className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer ${form.active ? 'bg-orange-500' : 'bg-gray-200'}`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.active ? 'left-5' : 'left-1'}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">Aktif</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <div
                onClick={() => set('popular', !form.popular)}
                className={`w-10 h-6 rounded-full transition-colors relative cursor-pointer ${form.popular ? 'bg-orange-500' : 'bg-gray-200'}`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.popular ? 'left-5' : 'left-1'}`} />
              </div>
              <span className="text-sm font-medium text-gray-700">Popüler</span>
            </label>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-xs font-medium px-3 py-2 rounded-lg bg-red-50 text-red-600">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className="ri-error-warning-line"></i>
              </div>
              {error}
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition-all cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-save-line"></i>
                </div>
              )}
              {isEdit ? 'Güncelle' : 'Ekle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;
